import React from "react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { Bot } from "@/components/icons";
import { Typography, Link } from "@/components/fortress";
import { PreviewImage } from "@/components/ui/image";
import { ProductReview } from "./ProductReview";
import { ProductRecommend } from "./ProductRecommend";
import { ComparisonTable } from "./Comparison";
import { CampaignCard } from "./CampaignCard";
import { CartCard } from "./CartCard";
import { OrderCard } from "./OrderCard";
import { ShipmentCard } from "./ShipmentCard";
import { cn, messageDateFormatter } from "@/lib/utils";
import {
  Conversation,
  MessageData,
  File,
  MessageTextContent,
  MessageFunctionContent,
} from "@/types";
import { ThumbUp } from "@/components/icons/ThumbUp";
import { ThumbDown } from "@/components/icons/ThumbDown";
import { useChatContext } from "../ChatContext";
import { MessageChangeEvent } from "@/lib/message";
import { ThumbUpFilled } from "@/components/icons/ThumbUpFilled";
import { ThumbDownFilled } from "@/components/icons/ThumbDownFilled";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  trackEvent,
} from "@/utils/analytics";
import { MESSAGE_API } from "@/config";
import { apiPost } from "@/utils";
import { MessageProvider } from "./MessageContext";
import { useSafeRemarkPlugins } from "@/hooks/useSafeRemarkPlugins";

interface MessageListProps {
  message: MessageData;
  isLatestMessage: boolean;
  onSendMessage: (message: string, isFromOption?: boolean) => void;
}

const toConversation = (message: MessageData): Conversation => {
  return {
    conversation_id: message.conversation_id,
    conversation_status: message.conversation_status,
  };
};

const CardWrapper = ({
  position,
  children,
}: {
  position: "left" | "right";
  children: React.ReactNode;
}) => {
  return (
    <div
      className={cn("px-3 py-2 rounded-md", {
        "rounded-tl-none": position === "left",
        "rounded-tr-none": position === "right",
        "border border-[#F6F3E7] bg-[#FBF9F4] text-foreground":
          position === "left",
        "border border-[#844025] bg-[#844025] text-white": position === "right",
      })}
    >
      {children}
    </div>
  );
};

export const ErrorContent = ({ position }: { position: "left" | "right" }) => {
  return (
    <CardWrapper position={position}>
      <Typography level="caption1">
        An unexpected error has occurred.
      </Typography>
    </CardWrapper>
  );
};

const TextContent = ({
  role,
  data,
}: {
  role: string;
  data: { text: string };
}) => {
  const remarkPlugins = useSafeRemarkPlugins([remarkBreaks]);

  return (
    <CardWrapper position={role === "user" ? "right" : "left"}>
      <Typography level="caption1" style={{ wordBreak: "break-word" }}>
        <Markdown
          remarkPlugins={remarkPlugins}
          components={{
            a: ({ href, children }) => (
              <Link
                variant="primary"
                color={role === "user" ? "white" : "warning"}
                size="xs"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </Link>
            ),
            p: ({ children, ...props }) => (
              <p className="first:mt-0 mt-[10px]" {...props}>
                {children}
              </p>
            ),
            li: ({ children, ...props }) => (
              <li className="list-disc ml-5" {...props}>
                {children}
              </li>
            ),
          }}
        >
          {data.text}
        </Markdown>
      </Typography>
    </CardWrapper>
  );
};

// NOTE: 当前只有用户侧的消息会使用 ImageContent
const ImageContent = ({ files }: { files: File[] }) => {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {files.map((file, index) => (
        <div
          key={index}
          className="w-20 h-20 border rounded-lg overflow-hidden"
        >
          <PreviewImage src={file.url} alt={file.name} />
        </div>
      ))}
    </div>
  );
};

const FunctionContent = ({
  conversation,
  data,
  message,
}: {
  conversation: Conversation;
  data: MessageFunctionContent;
  message: MessageData;
}) => {
  // 点击商品链接
  const handleProductClick = (product_sku_code: string) => {
    const params = {
      message_id: Number(message.message_id) || 0,
      product_sku_code: product_sku_code,
    };

    try {
      apiPost(MESSAGE_API.productVisit(), params);
    } catch (error) { }
  };

  return (
    <MessageProvider
      value={{
        message_id: Number(message.message_id) || 0,
        handleProductClick,
      }}
    >
      {data.type === "product_review" && <ProductReview data={data.data} />}
      {data.type === "get_shipment_list" && <ShipmentCard data={data.data} />}
      {data.type === "cart" && <CartCard data={data.data} />}
      {data.type === "campaign_list" && <CampaignCard data={data.data} />}
      {data.type === "product_comparison" && (
        <ComparisonTable conversation={conversation} data={data.data} />
      )}
      {data.type === "product_recommend" && (
        <ProductRecommend conversation={conversation} data={data.data} />
      )}
      {data.type === "get_order_list" && <OrderCard data={data.data} />}
    </MessageProvider>
  );
};

const QuickResponseContent = ({
  conversation,
  data,
  onSendMessage,
}: {
  conversation: Conversation;
  data: any;
  onSendMessage: (message: string, isFromOption?: boolean) => void;
}) => {
  return (
    <div className="flex flex-col space-y-2 mt-3">
      {data.data?.options?.map((option: any) => (
        <div
          key={`${conversation.conversation_id}-${option}`}
          className="border border-[#3C101E] rounded-[6px] py-2 px-4 w-fit cursor-pointer"
          onClick={() => onSendMessage(option, true)}
        >
          <Typography level="caption1" key={option}>
            {option}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const LikeOrNot = ({ message }: { message: MessageData }) => {
  const { onMessageChange } = useChatContext();
  const handleLikeClick = (isLike: boolean) => {
    const messageIsLike = message.is_like;
    // if the same click again, set to null
    const is_like = isLike === messageIsLike ? null : isLike;
    onMessageChange(new MessageChangeEvent(message, { is_like }));
  };
  return (
    <div className="flex space-x-4">
      <div className="flex items-center space-x-2 cursor-pointer">
        <div className="w-5 h-5" onClick={() => handleLikeClick(true)}>
          {message.is_like === true ? <ThumbUpFilled /> : <ThumbUp />}
        </div>
      </div>
      <div className="flex items-center space-x-2 cursor-pointer">
        <div className="w-5 h-5" onClick={() => handleLikeClick(false)}>
          {message.is_like === false ? <ThumbDownFilled /> : <ThumbDown />}
        </div>
      </div>
    </div>
  );
};

const MsgFooter = ({
  message,
  handleQuickResponseOptionClick,
}: {
  message: MessageData;
  handleQuickResponseOptionClick: (message: string) => void;
}) => {
  const quickResponse = message.content.map((contentItem, index) => {
    const { type, content } = contentItem;
    if ((content as MessageFunctionContent).type === "quick_response") {
      return (
        <QuickResponseContent
          key={`${type}-${index}`}
          conversation={toConversation(message)}
          data={content as MessageFunctionContent}
          onSendMessage={handleQuickResponseOptionClick}
        />
      );
    }
  });

  return (
    <div className="flex flex-col gap-[12px]">
      <div>{quickResponse}</div>
      <LikeOrNot message={message} />
    </div>
  );
};

const MsgContent = ({
  message,
  isLatestMessage,
  onSendMessage,
}: {
  message: MessageData;
  isLatestMessage: boolean;
  onSendMessage: (message: string, isFromOption?: boolean) => void;
}) => {
  const isUser = message.role === "user";

  const handleQuickResponseOptionClick = (message: string) => {
    trackEvent({
      event: TRACK_EVENT,
      category: "quick_response",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "quick_response_option",
    });
    onSendMessage(message, true);
  };

  const handleLikeClick = () => { };

  try {
    return (
      <>
        {/* 对话框图片 */}
        {message.files && message.files.length > 0 && (
          <ImageContent files={message.files} />
        )}

        {/* 消息框 */}
        <div className="flex flex-col space-y-3 max-w-full">
          {message?.content ? (
            message?.content?.map((contentItem, index) => {
              const { type, content } = contentItem;

              if (type === "text" && (content as MessageTextContent)?.text) {
                return (
                  <TextContent
                    key={`${type}-${index}`}
                    role={message.role}
                    data={content as MessageTextContent}
                  />
                );
              } else if (
                type === "function" &&
                (content as MessageFunctionContent).type !== "quick_response"
              ) {
                return (
                  <FunctionContent
                    key={`${type}-${index}`}
                    conversation={toConversation(message)}
                    data={content as MessageFunctionContent}
                    message={message}
                  />
                );
              }
            })
          ) : (
            <ErrorContent position={isUser ? "right" : "left"} />
          )}
        </div>

        {/* 时间戳 */}
        <div className="mt-3 text-[11px] text-[#87867F]">
          {message.created_at && messageDateFormatter(message.created_at)}
        </div>

        {/* 快捷回复 */}
        {/* TODO: 应该拆分 */}
        {isLatestMessage &&
          message.role === "assistant" &&
          message?.content && (
            <MsgFooter
              message={message}
              handleQuickResponseOptionClick={handleQuickResponseOptionClick}
            />
          )}
      </>
    );
  } catch (error) {
    return <ErrorContent position={isUser ? "right" : "left"} />;
  }
};

export const Message: React.FC<MessageListProps> = ({
  message,
  isLatestMessage,
  onSendMessage,
}) => {
  const isUser = message.role === "user";

  return (
    <div
      className={cn("flex mb-4", {
        "justify-start": !isUser,
        "justify-end": !!isUser,
      })}
    >
      <div
        className={cn("flex-1 flex flex-col max-w-full", {
          "items-start": !isUser,
          "items-end": !!isUser,
        })}
      >
        {/* 头像 */}
        {!isUser && (
          <div className="flex items-center space-x-2 mb-2">
            <div className="flex-shrink-0 flex items-center justify-center w-5 h-5 text-xs text-white rounded-full bg-[#844025]">
              <div className="w-3 h-3 font-[12px]">
                <Bot />
              </div>
            </div>
            <div className="text-[14px] text-[#844025]">Casa</div>
          </div>
        )}

        <MsgContent
          message={message}
          isLatestMessage={isLatestMessage}
          onSendMessage={onSendMessage}
        />
      </div>
    </div>
  );
};
